import { ArrowPathIcon } from '@heroicons/react/24/outline'

import { SYSTEM_DISCOVERY_APP_NAME } from '../../../features/ConfigureDataCollectionAdminPanelPage/systemDiscoveryUtils'
import type {
  AppMetaFormData,
  ConfigTestCase,
} from '../../../features/ConfigureDataCollectionAdminPanelPage/types'
import type {
  DataCollectionRuleFormData,
  DataCollectionTestCaseFormData,
  RuleIn,
} from '../../../features/ConfigureDataCollectionAdvancedAdminPanelPage/types'
import { Button, Text } from '../../designs'
import { ContentDisplay } from '../displays'
import DataCollectionAppForm from './DataCollectionAppForm'
import DataCollectionGranularity from './DataCollectionGranularity'
import DataCollectionRules from './DataCollectionRules'
import DataCollectionTests from './DataCollectionTests'
import type { AppTestCaseGroup, DataCollectionGranularityForm } from './types'

interface Props {
  appTestCasesGroup: AppTestCaseGroup
  appMetaFormData: AppMetaFormData
  isLoadingUpdateAppMeta: boolean
  onUpdateAppData: (appData: AppMetaFormData) => Promise<void>
  granularityFormData: DataCollectionGranularityForm
  onUpdateGranularityData: (formData: DataCollectionGranularityForm) => Promise<void>
  onAddTestCase: (formValues: DataCollectionTestCaseFormData) => Promise<void>
  onUpdateTestCase: (
    formValues: DataCollectionTestCaseFormData,
    configTestCase: ConfigTestCase,
  ) => Promise<void>
  onDeleteTestCase: (testCase: ConfigTestCase) => Promise<void>
  onAddRuleIn: (formValues: DataCollectionRuleFormData) => Promise<void>
  onUpdateRuleIn: (formData: DataCollectionRuleFormData, originalRule: RuleIn) => Promise<void>
  onUpdateRuleInCode: (newRule: RuleIn) => Promise<void>
  onDeleteRuleIn: (rule: RuleIn) => Promise<void>
  isAppDeleteEnabled: boolean
  onDeleteApp: () => Promise<void>
  onSyncSystemDiscoveryExclusions?: () => Promise<void>
  isSyncingSystemDiscovery?: boolean
}

const AppLevelTestAndRules = ({
  appTestCasesGroup,
  appMetaFormData,
  onUpdateAppData,
  isLoadingUpdateAppMeta,
  granularityFormData,
  onUpdateGranularityData,
  onAddTestCase,
  onUpdateTestCase,
  onDeleteTestCase,
  onAddRuleIn,
  onUpdateRuleIn,
  onUpdateRuleInCode,
  onDeleteRuleIn,
  isAppDeleteEnabled,
  onDeleteApp,
  onSyncSystemDiscoveryExclusions,
  isSyncingSystemDiscovery,
}: Props) => {
  const isSystemDiscovery = appTestCasesGroup.appName === SYSTEM_DISCOVERY_APP_NAME

  return (
    <div className='space-y-8'>
      <div className='space-y-2'>
        <Text size='l' weight={500}>
          Application Rules
        </Text>
        <DataCollectionAppForm
          initialAppFormData={appMetaFormData}
          onUpdateAppData={onUpdateAppData}
          isLoading={isLoadingUpdateAppMeta}
        />
        {isSystemDiscovery && onSyncSystemDiscoveryExclusions && (
          <div className='rounded-md bg-blue-50 p-4'>
            <div className='flex items-start'>
              <div className='flex-1'>
                <Text size='s' weight={500} className='mb-1'>
                  Sync Tracked App Exclusions
                </Text>
                <Text size='xs' color='gray'>
                  Update System Discovery to exclude all currently tracked applications. This
                  prevents duplicate data collection for apps that already have specific rules.
                </Text>
              </div>
            </div>
            <div className='mt-3'>
              <Button
                size='s'
                onClick={onSyncSystemDiscoveryExclusions}
                loading={isSyncingSystemDiscovery ?? false}
                iconStart={<ArrowPathIcon />}
              >
                Sync Exclusions
              </Button>
            </div>
          </div>
        )}
        {isAppDeleteEnabled && (
          <div className='flex justify-end'>
            <Button variant='destructive' onClick={onDeleteApp}>
              Delete App
            </Button>
          </div>
        )}
      </div>
      <div className='space-y-2'>
        <Text size='l' weight={500}>
          Data Collection Granularity
        </Text>
        {appTestCasesGroup.rules.length > 0 && (
          <div className='space-y-2'>
            <DataCollectionGranularity
              formData={granularityFormData}
              onUpdateGranularityData={onUpdateGranularityData}
              appName={appTestCasesGroup.appName}
            />
          </div>
        )}
      </div>
      <div className='space-y-2'>
        <Text size='l' weight={500}>
          Data Collection Rules
        </Text>
        <ContentDisplay className='p-4'>
          <div className='w-full md:grid md:grid-cols-2 md:gap-4'>
            <DataCollectionTests
              testCases={appTestCasesGroup.testCases}
              onAddTestCase={onAddTestCase}
              onUpdateTestCase={onUpdateTestCase}
              onDeleteTestCase={onDeleteTestCase}
            />
            <DataCollectionRules
              rulesForTestCases={appTestCasesGroup.rules}
              onAddRuleIn={onAddRuleIn}
              onUpdateRuleIn={onUpdateRuleIn}
              onUpdateRuleInCode={onUpdateRuleInCode}
              onDeleteRuleIn={onDeleteRuleIn}
            />
          </div>
        </ContentDisplay>
      </div>
    </div>
  )
}

export default AppLevelTestAndRules

